import { Fragment, useEffect, useRef, useState } from 'react';
import {
  ModalHeader,
  ModalContentWrapper,
  ModalBody,
  ModalActionContainer,
  StyledForm,
  TicketHolderContainer,
  TicketRemovalContainer,
  TicketHolder,
  ActionsContainer,
  BadgesContainer
} from "./Modal.styles";
import { Button } from "../Button/Button";
import { Icon } from '../../icons';
import { Badge, InfoMessage, RemovalConfirmation, TicketDetails, TicketFormCard, TicketFormCardProps, TicketIcon } from '..';
import { IBookingInterestUpdate, IBookingTicket, IRelatedBookingInterestUpdate, IUserBookingTicket } from '../../models/BookingTicket';
import { Box, debounce } from '@mui/material';
import Link from 'next/link';
import { ModalBookingData, ModalTexts, useModal } from '../../contexts/ModalContext';
import { emailValidation } from '../../utils/regex';
import { deleteBookingInterestTicket, updateGuestsBookingInterest } from '../../services/BookingInterestService';
import { adultAgeLimit } from '../../utils/constants';

interface ContentProps {
  closeModal: () => void;
  onClick?: (successful?: boolean, message?: React.ReactElement | string) => Promise<void> | void;
  modalBookingData?: ModalBookingData;
  modalTexts?: ModalTexts;
}

type ChangedDataObj = {
  [key: string]: Partial<IBookingTicket>;
}

export const ModalContent = {
  None: () => {
    return (
      <Fragment />
    )
  },
  TermsHighlight: ({ closeModal, onClick }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header"><span>Please Remember!</span> You've Agreed:</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            <ul>
              <li key="1">Tickets <span>can not be sold or transferred.</span> Having tickets does not guarantee you entry to the show</li>
              <li key="2">You <span>won't take any pictures</span> inside the studio or share any spoilers before the show airs</li>
              <li key="3">You <span>can be filmed</span> as part of the audience and the footage will be used as part of the show</li>
            </ul>
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient3"
            onClick={() => onClick && onClick()}>
            Ok, Got it!
          </Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  NonPrioritySessionSelected: ({ closeModal, onClick }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Heads Up! You’ve Selected a Non-Priority Session</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            You can still proceed with your booking but note this is a normal session with <span>no priority entry</span> and so <span>ticket availability is not guaranteed</span> (it is for priority sessions)
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container justify-between">
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient1"
            onClick={() => onClick && onClick(true)}
          >
            View Priority Sessions
          </Button>
          <Button onClick={() => onClick && onClick(false)} variant="tertiary" >Continue with Non-Priority</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  PriorityTicketsFullyBooked: ({ closeModal, onClick }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">You Just Missed Out! Priority Tickets are Fully Booked 😢</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Sadly all priority tickets have now been booked (they go like hot cakes). But don’t worry, you can still apply for a regular session to see the show live:
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient1"
            onClick={() => onClick && onClick(true)}>
            View Priority Sessions
          </Button>
          <Button onClick={() => onClick && onClick(false)} variant="tertiary" >Continue with Non-Priority</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  NotEnoughPriorityTickets: ({ closeModal, onClick }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">We don't have enough Priority tickets 😢</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Sadly you requested more priority tickets than we have left. Either reduce the ticket number or apply for General Admission tickets. 
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button
            onClick={closeModal}>
            Edit current tickets
          </Button>
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient1"
            onClick={() => onClick && onClick()}>
            Continue with General Admission tickets
          </Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  DeleteAccount: ({ closeModal }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Are You Sure?</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Deleting your account is permanent and can't be undone. You'll lose all current applications, ticket offers and tickets.
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Nooo! Keep My Account</Button>
          <Button data-cy="modal-forwardAction-button" gradientStyle="gradient3" onClick={closeModal}> Yes, Delete My Account</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  SaveProgress: ({ closeModal, onClick }: ContentProps) => {
    const handleExitPage = () => {
      if (onClick) {
        onClick();
      }
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Are You Sure?</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            If you leave the page, any changes you have made will be lost.
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Nooo! Continue Editing</Button>
          <Button data-cy="modal-forwardAction-button" gradientStyle="gradient3" onClick={handleExitPage}> Yes, Close Without Saving</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  DeactivateAccount: ({ closeModal }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Deactivate Your Account</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            We need to keep your details on file for X amount of time to adhere to legal requirements for any tickets you have accepted and shows you have attended.<br />
            <br />
            With a deactivated account you won't be able to apply for or receive any tickets and won't receive any notifications from us about new shows etc.<br />
            <br />
            You can reactivate at any time before X time. Once X time has been reached, your account will be permanently deleted.<br />
            <br />
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" size="sm" onClick={closeModal}>No, Keep My Account Active</Button>
          <Button data-cy="modal-forwardAction-button" gradientStyle="gradient2" size="sm" onClick={closeModal}>Yes, Deactivate Account</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  ReactivateAccount: ({ closeModal }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Reactivate Your Account?</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Your account is currently deactivated and will be permanently deleted in X time.<br />
            <br />
            You can't apply for or receive any tickets and won't receive any notifications from us about new shows etc. unless you reactivate your account.
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" size="sm" onClick={closeModal}>Leave Account Deactivated</Button>
          <Button data-cy="modal-forwardAction-button" gradientStyle="gradient1" size="sm" onClick={closeModal}>Reactivate My Account</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  ResetPassword: ({ closeModal }: ContentProps) => {
    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Password Reset Email Sent!</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Check your inbox for the reset email we just sent
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button
            data-cy="modal-forwardAction-button"
            variant="tertiary"
            size="md"
            onClick={closeModal}
          >
            <span>Go to Your Email Inbox <Icon icon="ArrowRight" size="sm" /></span>
          </Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  EditGuestDetails: ({ closeModal, onClick, modalBookingData }: ContentProps) => {
    const bookingInterest = modalBookingData!.bookingInterest;

    const [userBookingTicket, setUserBookingTicket] =
      useState<IUserBookingTicket>({
        id: bookingInterest.id,
        bookForAnotherPerson: false,
        loggedInUserTicket: {
          id: bookingInterest.loggedInUserTicket.id,
          firstName: bookingInterest.loggedInUserTicket.firstName,
          lastName: bookingInterest.loggedInUserTicket.lastName,
          email: bookingInterest.loggedInUserTicket.email,
          age: bookingInterest.loggedInUserTicket.age,
          accessibility: bookingInterest.loggedInUserTicket.accessibility ?? "None",
          otherAccessibility: bookingInterest.loggedInUserTicket.otherAccessibility ?? ""
        },
        ticketForAnotherPerson: bookingInterest.ticketForAnotherPerson.map(bookingInterest => {
          return {
            id: bookingInterest.id,
            firstName: bookingInterest.firstName,
            lastName: bookingInterest.lastName,
            email: bookingInterest.email,
            age: bookingInterest.age,
            accessibility: bookingInterest.accessibility ?? "None",
            otherAccessibility: bookingInterest.otherAccessibility ?? "",
            status: bookingInterest.status
          }
        }),
        error: false,
      });

    const [genericError, setGenericError] = useState(false);
    const [updateError, setUpdateError] = useState(false);
    const [updateErrorText, setUpdateErrorText] = useState('');
    const [nextDisabled, setNextDisabled] = useState(true);
    const [canScroll, setCanScroll] = useState(true);
    const [loading, setLoading] = useState(false);

    const scrollableElemRef = useRef<HTMLFormElement | null>(null);
    const ticketFormCardRef = useRef<TicketFormCardProps>(null);

    const triggerValidation = () => {
      if (ticketFormCardRef.current && ticketFormCardRef.current.validate) {
        ticketFormCardRef.current.validate();
      }
    };

    useEffect(() => {
      const handleResize = debounce(() => {
        if (scrollableElemRef.current) {
          const canScroll = scrollableElemRef.current.scrollHeight > scrollableElemRef.current.clientHeight;
          setCanScroll(canScroll);
        }
      }, 200);

      handleResize();

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [scrollableElemRef.current]);


    const handleFormChange = (ticket: IBookingTicket, index: number) => {
      let currentObjects = userBookingTicket.ticketForAnotherPerson;

      if (currentObjects?.length > 0) {
        currentObjects[index] = ticket;

        validateTicketHolders(currentObjects);

        setUserBookingTicket({
          ...userBookingTicket,
          ticketForAnotherPerson: currentObjects
        });
      }
    };

    const handleSubmitButton = async () => {
      setUpdateError(false);
      setLoading(true);
      let loggedInUser = userBookingTicket.loggedInUserTicket
      loggedInUser.accessibility = loggedInUser.accessibility === "None" ? null : loggedInUser.accessibility
      let bookingInterestToUpdate = {
        id: userBookingTicket.id,
        user: loggedInUser,
        relatedBookingInterests: userBookingTicket.ticketForAnotherPerson.map(
          (ticket: IBookingTicket) => {
            ticket.accessibility = ticket.accessibility === "None" ? null : ticket.accessibility
            ticket.otherAccessibility = ticket.otherAccessibility === "" ? null : ticket.otherAccessibility
            ticket.email = ticket.age >= 18 ? ticket.email : ""
            return { user: ticket };
          }
        ),
      } as IBookingInterestUpdate;

      const modifiedFields = trackFieldChanges(bookingInterestToUpdate);
      let modifiedEmailGuests: string[] = [];

      Object.entries(modifiedFields).forEach(([index, modifiedValuesObj]) => {
        if (modifiedValuesObj.hasOwnProperty("email")) {
          modifiedEmailGuests.push(modifiedValuesObj.email!)
        }
      });

      const res = await updateGuestsBookingInterest(bookingInterestToUpdate)
      if (res.error) {
        setUpdateError(true);
        if (typeof res.error === "string") {
          setUpdateErrorText(res.error);
        } else {
          setUpdateErrorText(res.error.title);
        }
      } else {
        if (onClick) {
          const message = (
            <>
              Tickets updated successfully!
              {modifiedEmailGuests.length > 0 && (
                <>
                  <br />
                  Email has been sent to the following guests:
                  <br />
                  {modifiedEmailGuests.map(email => {
                    return <>{email}<br /></>
                  })}
                </>
              )}
            </>
          );
          onClick(true, message);
        }
        closeModal();
      }
      setLoading(false);
    }

    const handleDisabledClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation;

      if (nextDisabled) {
        setGenericError(true);
        triggerValidation();
      } else {
        setGenericError(false);
        handleSubmitButton();
      }
    };

    const validateTicketHolders = (object: any) => {
      setUpdateError(false);
      for (let i in object) {
        if (!(object[i].firstName == "" ||
          object[i].lastName == "" ||
          (object[i].age >= 18 && (object[i].email == "" || !emailValidation(object[i].email))) ||
          object[i].age == 0 ||
          object[i].accessibility == "")) {
          setNextDisabled(false);
          setGenericError(false);
        } else {
          setNextDisabled(true);
          break
        }
      }
    }

    const trackFieldChanges = (newBooking: IBookingInterestUpdate) => {
      const oldBookingsMapped = bookingInterest.ticketForAnotherPerson.map(
        (ticket: IBookingTicket) => {
          ticket.accessibility = ticket.accessibility === "None" ? null : ticket.accessibility
          ticket.otherAccessibility = ticket.otherAccessibility === "" ? null : ticket.otherAccessibility
          ticket.email = ticket.age >= 18 ? ticket.email : ""
          return { user: ticket } as IRelatedBookingInterestUpdate;
        }
      );

      let changedDataObj: ChangedDataObj = {}

      newBooking.relatedBookingInterests?.forEach((newBooking, index: number) => {
        const oldBookingUser = oldBookingsMapped[index].user;
        const newBookingUser = newBooking.user;

        let changedData: any = {};

        for (const key in oldBookingUser) {
          if (newBookingUser.hasOwnProperty(key)) {
            const oldValue = oldBookingUser[key as keyof IBookingTicket];
            const newValue = newBookingUser[key as keyof IBookingTicket];

            if (oldValue !== newValue) {
              changedData[key as keyof IBookingTicket] = newValue
            }
          }
        }

        if (Object.entries(changedData).length !== 0) {
          changedDataObj[index] = changedData;
        }
      });

      return changedDataObj;
    }

    const numberOfGuests = bookingInterest.ticketForAnotherPerson.length;

    const isTicketDisabled = (ticket: IBookingTicket) => {
      if (ticket.age < 18)
        return false;
      return !!ticket.status && ticket.status !== "InterestRegistered" && ticket.status !== "InvitedToTicket"
    }

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Edit Guest Details</ModalHeader>
        <ModalContentWrapper sx={{ position: "initial" }} className={`modal-content-wrapper ${canScroll ? "scrollable" : ''}`}>
          <ModalBody className="modal-body">
            {numberOfGuests > 0 ? `Edit details for the ${numberOfGuests} guest tickets on this application` : "No guests invited on this application."}
          </ModalBody>
          <StyledForm ref={scrollableElemRef}>
            <Box>
              <TicketFormCard
                index={-1}
                variant='unstyled'
                key={'edit-guests-userTicket-main'}
                ticketNumber={1}
                disabled={true}
                disableAccessibilityDropdown
                bookingTicket={userBookingTicket.loggedInUserTicket}
                isPriority={modalBookingData?.isPriority}
              />
              <InfoMessage message={<>The Lead Booker cannot be edited. Please email <Link href="mailto:audience@thatstheticket.com.au" target="_blank" rel="noopener noreferrer">audience@thatstheticket.com.au</Link> with any changes needed.</>} />
            </Box>

            {userBookingTicket.ticketForAnotherPerson.map((otherTicket, index) =>
              <Box key={`edit-guests-otherTickets-${index}`}>
                <TicketFormCard
                  data-cy="interest-form-row"
                  variant="unstyled"
                  index={index}
                  bookingTicket={otherTicket}
                  ticketNumber={index + 2}
                  handleFormChange={(ticket: IBookingTicket, index: number) =>
                    handleFormChange(ticket, index)
                  }
                  minAge={modalBookingData!.minAge}
                  disabled={isTicketDisabled(otherTicket)}
                  disableAccessibilityDropdown
                  loggedInUserEmail={userBookingTicket.loggedInUserTicket.email}
                  isPriority={modalBookingData?.isPriority}
                  ref={ticketFormCardRef}
                />
                {isTicketDisabled(otherTicket) && (
                  <InfoMessage message="This booking cannot be edited. Guest already responded to the offer" />
                )}
              </Box>
            )}
          </StyledForm>
          <InfoMessage hasBackground message={<>If your guest has already registered we'll take their data from their profile.</>} />
          {genericError && (
            <InfoMessage variant="error" message="Hang fire!! Please address the issues highlighted above before continuing 👆" />
          )}
          {updateError && (
            <InfoMessage variant="error" message={updateErrorText} />
          )}
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" size="sm" onClick={closeModal}>Cancel</Button>
          <div onClick={handleDisabledClick}>

            <Button
              data-cy="modal-forwardAction-button"
              disabled={nextDisabled}
              gradientStyle="gradient1"
              loading={loading}
              size="sm">
              Save Details
            </Button>
          </div>
        </ModalActionContainer>
      </Fragment>
    )
  },
  RemoveTickets: ({ closeModal, onClick }: ContentProps) => {
    const { modalBookingData } = useModal();

    const bookingInterest = modalBookingData!.bookingInterest;
    const LoggedInUserBookingInterest = bookingInterest.loggedInUserTicket;
    const OtherBookingInterests = bookingInterest.ticketForAnotherPerson;
    const [deleteError, setDeleteError] = useState(false);
    const [deleteErrorText, setDeleteErrorText] = useState('');
    const [selectedId, setSelectedId] = useState(0);
    const removeTicket = async (bookingInterestId: number) => {
      setDeleteError(false)
      setSelectedId(bookingInterestId)
      const res = await deleteBookingInterestTicket(bookingInterestId)
      if (res === undefined) {
        if (onClick) {
          onClick(true)
        }
        if (bookingInterestId === bookingInterest.id) {
          closeModal();
        }
      } else {
        setDeleteError(true)
        setDeleteErrorText("Cannot delete Ticket because either you have people who depend on it, or you've already Accepted. Please contact support.")
      }
      setSelectedId(0)
    }

    const isTicketDisabled = (ticket: IBookingTicket) => {
      if (ticket.age < 18)
        return false;
      return !!ticket.status && ticket.status !== "InterestRegistered" && ticket.status !== "InvitedToTicket"
    }

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Remove Tickets</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            Remove tickets on this application
          </ModalBody>
          <TicketRemovalContainer>
            <TicketHolderContainer>
              <TicketIcon
                ticketNumber={1}
                ticketIconStyle='dotted' />
              <TicketHolder>{LoggedInUserBookingInterest.firstName} {LoggedInUserBookingInterest.lastName}</TicketHolder>
              <BadgesContainer>
                <Box>
                  <Badge badgeStyle="leadBooker" />
                </Box>
                <Box>
                  {modalBookingData?.isPriority ? (
                    <Badge badgeStyle="priority" size="sm" />
                  ) : (!!LoggedInUserBookingInterest.accessibility && LoggedInUserBookingInterest.accessibility != "None") || !!LoggedInUserBookingInterest.otherAccessibility ? (
                    <Badge badgeStyle="accessibilityRequired" size="sm" />
                  ) : (
                    <Badge badgeStyle="generalAdmission" size="sm" />
                  )}
                </Box>
              </BadgesContainer>
              <ActionsContainer>
                <RemovalConfirmation loading={selectedId === bookingInterest.id} disabled={LoggedInUserBookingInterest.status === "TicketAccepted"} primaryButtonText="Remove Ticket?" cancelButtonText="Keep Ticket" onPrimaryButtonClick={() => removeTicket(bookingInterest.id!)} />
              </ActionsContainer>
              {LoggedInUserBookingInterest.status === "TicketAccepted" && (
                <InfoMessage message="This ticket cannot be deleted. You already accepted the offer" />
              )}
            </TicketHolderContainer>

            {OtherBookingInterests.map((otherTicket, index) => (
              <>
                <TicketHolderContainer key={`edit-guests-otherTickets-${index}`}>
                  <TicketIcon
                    ticketNumber={index + 2}
                    ticketIconStyle={isTicketDisabled(otherTicket) ? "outlined" : "dotted"}
                  />
                  <TicketHolder>{otherTicket.firstName} {otherTicket.lastName}</TicketHolder>
                  <BadgesContainer>
                    <Box>
                      {otherTicket.age < adultAgeLimit &&
                        <Badge badgeStyle="underAge" />}
                    </Box>
                    <Box>
                      {modalBookingData?.isPriority ? (
                        <Badge badgeStyle="priority" size="sm" />
                      ) : (!!otherTicket.accessibility && otherTicket.accessibility != "None") || !!otherTicket.otherAccessibility ? (
                        <Badge badgeStyle="accessibilityRequired" size="sm" />
                      ) : (
                        <Badge badgeStyle="generalAdmission" size="sm" />
                      )}
                    </Box>
                  </BadgesContainer>
                  <ActionsContainer>
                    <RemovalConfirmation loading={selectedId === otherTicket.bookingInterestId} disabled={isTicketDisabled(otherTicket)} primaryButtonText="Remove Ticket?" cancelButtonText="Keep Ticket" onPrimaryButtonClick={() => removeTicket(otherTicket.bookingInterestId!)} />
                  </ActionsContainer>
                  {isTicketDisabled(otherTicket) && (
                    <InfoMessage message="This ticket cannot be deleted. Guest already responded to the offer" />
                  )}
                </TicketHolderContainer>

              </>
            ))}
          </TicketRemovalContainer>
          <InfoMessage hasBackground message={<>Be careful. You cannot readd a person once you’ve deleted them</>} />
          {deleteError && (
            <InfoMessage variant="error" message={deleteErrorText} />
          )}
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" size="sm" onClick={closeModal}>Cancel</Button>
          <Button data-cy="modal-forwardAction-button" gradientStyle="gradient1" size="sm" onClick={closeModal}>
            Finished
          </Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  AcceptTicket: ({ closeModal, onClick }: ContentProps) => {
    const { modalTexts } = useModal();
    const [loading, setLoading] = useState(false);

    const handleAccept = async () => {
      setLoading(true)
      if (onClick) {
        await onClick();
      }
      setLoading(false)
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Are You Sure?</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            {modalTexts.mainBody}
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button data-cy="modal-forwardAction-button" loading={loading} gradientStyle="gradient3" onClick={handleAccept}>I understand, and Accept</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  DeclineTicket: ({ closeModal, onClick }: ContentProps) => {
    const [loading, setLoading] = useState(false);

    const declineTicket = async () => {
      setLoading(true)
      if (onClick) {
        await onClick();
      }
      setLoading(false)
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header">Are You Sure?</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            You are going to cancel some tickets. Caution: This action is permanent and can’t be undone.
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button data-cy="modal-forwardAction-button" loading={loading} gradientStyle="gradient3" onClick={declineTicket}>Yes, Continue</Button>
        </ModalActionContainer>
      </Fragment>
    )
  },
  AccessibilityRequirementsChange: ({ closeModal, onClick }: ContentProps) => {
    const handleUpdate = async () => {
      if (onClick) {
        await onClick();
      }
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header"><span>Important!</span> Before you proceed:</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            <ul>
              <li key="1">Changing your Access Requirements <span>will not change any CURRENT Tickets or Applications.</span></li>
              <li key="2">You need to cancel existing tickets or ticket requests and <span>REAPPLY for those sessions</span> to get an Accessibility Required ticket.</li>
              <li key="3">Venues have limited Accessibility seats. You may <span>not get a ticket</span> to a show that you previously accepted or applied for tickets to.</li>
            </ul>
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient3"
            onClick={() => handleUpdate()}
          >
            UPDATE MY ACCESS REQUIREMENTS
          </Button>
        </ModalActionContainer>
      </Fragment>
    );
  },
  AllocationAccessibilityRequirementsAdded: ({ closeModal, onClick }: ContentProps) => {
    const handleUpdate = async () => {
      if (onClick) {
        await onClick();
      }
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header"><span>Important!</span> Before you proceed:</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            <ul>
              <li key="1">Any addition of access requirements <span>is subject to approval and will be withheld until such approval is granted.</span></li>
              <li key="2">Venues have limited Accessibility seats.</li>
            </ul>
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient3"
            onClick={() => handleUpdate()}
          >
            UPDATE GUEST DETAILS
          </Button>
        </ModalActionContainer>
      </Fragment>
    );
  },
  AllocationAccessibilityRequirementsRemoved: ({ closeModal, onClick }: ContentProps) => {
    const handleUpdate = async () => {
      if (onClick) {
        await onClick();
      }
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header"><span>Important!</span> Before you proceed:</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            <ul>
              <li key="1">The removal of access requirements <span>is permitted and the guest will receive an updated ticket.</span></li>
              <li key="2">Venues have limited Accessibility seats. <span>ADDING accessibility later will need approval </span>and result in the guest losing their ticket.</li>
            </ul>
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient3"
            onClick={() => handleUpdate()}
          >
            UPDATE GUEST DETAILS
          </Button>
        </ModalActionContainer>
      </Fragment>
    );
  },
  AllocationUpdatedAge: ({ closeModal, onClick }: ContentProps) => {
    const handleUpdate = async () => {
      if (onClick) {
        await onClick();
      }
      closeModal();
    };

    return (
      <Fragment>
        <ModalHeader data-cy="modal-header" className="modal-header"><span>Important!</span> Before you proceed:</ModalHeader>
        <ModalContentWrapper className="modal-content-wrapper">
          <ModalBody className="modal-body">
            <ul>
              <li key="1">Some dependent guests are <span>under the show minimum age and the tickets will be withheld until approved by an admin.</span></li>
            </ul>
          </ModalBody>
        </ModalContentWrapper>
        <ModalActionContainer className="modal-button-container">
          <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          <Button
            data-cy="modal-forwardAction-button"
            gradientStyle="gradient3"
            onClick={async () => { await handleUpdate() }}
          >
            UPDATE GUEST DETAILS
          </Button>
        </ModalActionContainer>
      </Fragment>
    );
  }
};
